import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SectionHeading from '@/components/feature/SectionHeading';
import MovieCard from '@/components/feature/MovieCard';
import NewsCard from '@/components/feature/NewsCard';
import { useSeo } from '@/hooks/useSeo';
import { latestMovies, newsList } from '@/utils/media';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryQuickLinks from '@/pages/home/components/CategoryQuickLinks';
import HomeSidebar from '@/pages/home/components/HomeSidebar';

export default function Home() {
  useSeo({
    title: '91爱爱-最新影视资源与影视资讯平台',
    description:
      '91爱爱提供丰富的影视资源和最新影视资讯，覆盖电影、电视剧等多种类型，满足用户多样化观看需求。',
    keywords: '91爱爱,影视资源,电影,电视剧,影视资讯,在线视频,影视推荐,影视平台',
  });

  const latest = latestMovies(8);
  const hotNews = newsList.slice(0, 5);

  return (
    <SiteLayout>
      <HeroCarousel />

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
          <div className="flex min-w-0 flex-col gap-10">
            <section aria-label="分类快捷入口">
              <SectionHeading
                title="分类快捷入口"
                subtitle="按类型快速找到你想看的影视作品"
                icon="ri-layout-grid-line"
              />
              <CategoryQuickLinks />
            </section>

            <section aria-label="最新影视资源">
              <SectionHeading
                title="最新影视资源"
                subtitle="按更新时间倒序展示最新上传影片"
                icon="ri-time-line"
                moreTo="/category/hot"
              />
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4">
                {latest.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </section>

            <section aria-label="热门影视资讯精选">
              <SectionHeading
                title="热门影视资讯精选"
                subtitle="影视行业动态、影人报道与市场观察"
                icon="ri-newspaper-line"
                moreTo="/news"
              />
              <div className="grid gap-3 sm:grid-cols-2">
                {hotNews.map((article) => (
                  <NewsCard key={article.id} article={article} variant="card" />
                ))}
              </div>
            </section>
          </div>

          <HomeSidebar />
        </div>
      </div>

      <section className="bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-14">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                <i className="ri-shield-star-line" />
                平台简介
              </span>
              <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                91爱爱 · 最新影视资源与影视资讯平台
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                91爱爱专注为影视爱好者提供及时、丰富的影视资源展示与权威资讯阅读服务。平台覆盖电影、电视剧等多种类型，
                通过分类浏览、关键词搜索与多维筛选，让你更快找到想看的内容；同时以资讯报道呈现行业动态与影人故事。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                本站为演示站点，所有影视信息与资讯内容均为虚构示例，仅用于展示平台结构，不提供在线播放与下载功能。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to="/category/movie"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
                >
                  <i className="ri-film-line" />
                  浏览影视资源
                </Link>
                <Link
                  to="/news"
                  className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-newspaper-line" />
                  阅读影视资讯
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '21', label: '收录影视资源', icon: 'ri-movie-2-line' },
                { value: '10', label: '精选资讯报道', icon: 'ri-article-line' },
                { value: '4', label: '热门题材分类', icon: 'ri-price-tag-3-line' },
                { value: '每日', label: '资源更新频率', icon: 'ri-refresh-line' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${stat.icon} text-xl`} />
                  </span>
                  <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">{stat.value}</p>
                  <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}